import { FileText } from "lucide-react";
import type { BuyerSendVersionDto } from "@/api/buyerSendVersions";
import { Timestamp } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { CarriedInBadge, pathLabel, versionLabel } from "./sendVersionBits";

/**
 * The package's history, as PREVIEW CARDS.
 *
 * <p><b>Why cards here and a dense table for the intro email.</b> A package is a
 * document. What distinguishes two of them is which letter they enclosed and
 * what was attached — facts that want a little room and read badly as narrow
 * columns. The intro email is text and nothing else, so its distinguishing facts
 * are short scalars and a table compares them faster than any card can.</p>
 *
 * <p>🔴 <b>No thumbnail and no page count, deliberately.</b> A package PDF is not
 * stored — `BuyerPackageService.GetPdfAsync` re-renders it on demand from the
 * submission's CURRENT narrative, terms and letter — so the only first page this
 * app can produce for an old version is one that did not exist when that version
 * was sent. Putting it on a March card would show a reader a page March's broker
 * never received, while looking exactly like evidence. The card shows what is
 * known first-hand instead. See the note in `sendVersionBits` for what a real
 * thumbnail would cost.</p>
 */
export function PackageVersionCards({
  versions,
  selectedId,
  onSelect,
}: {
  versions: BuyerSendVersionDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <ul className="grid gap-space-2 list-none p-0 m-0">
      {versions.map((v) => {
        const isSelected = v.id === selectedId;
        const attachment = v.attachmentNames[0] ?? null;

        return (
          <li key={v.id}>
            <button
              type="button"
              onClick={() => onSelect(v.id)}
              aria-current={isSelected ? "true" : undefined}
              className={cn(
                "w-full text-left grid gap-space-2 rounded-card border p-space-3",
                "transition-colors",
                isSelected
                  ? "border-gold bg-gold/10"
                  : "border-gray-mid/30 bg-white-pure hover:border-gold/60",
              )}
            >
              <div className="flex items-center justify-between gap-space-2 min-w-0">
                <span className="inline-flex items-center gap-space-2 font-medium">
                  {versionLabel(v)}
                  {v.recordedBeforeVersioning ? <CarriedInBadge /> : null}
                </span>
                <span className="text-caption text-gray-mid whitespace-nowrap">
                  {v.selfAttested === null ? "Not recorded" : pathLabel(v)}
                </span>
              </div>

              <div className="text-small text-gray-mid">
                <Timestamp value={v.sentAtUtc} />
                {v.toEmail ? <> · {v.toEmail}</> : null}
              </div>

              {/* The attachment stands in for the document itself: it is the
                  one first-hand fact about what was enclosed. */}
              <div className="flex items-center gap-space-2 text-small min-w-0">
                <FileText className="h-4 w-4 shrink-0 text-gray-mid" aria-hidden />
                {attachment ? (
                  <span className="truncate">{attachment}</span>
                ) : (
                  <span className="text-gray-mid italic">
                    {v.selfAttested
                      ? "Attached from your own email"
                      : "No attachment recorded"}
                  </span>
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
